import './MarketsPage.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Flame, BarChart2, Building2, Search, Filter, 
  ChevronDown, Check, RefreshCw, Compass, Newspaper, Globe, ExternalLink,
  Activity, Clock, Zap, Grid3X3, List, ArrowUpRight, ArrowDownRight,
  AlertTriangle, DollarSign, Layers
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import Sparkline, { generateSparklineData } from '../components/Sparkline';
import { getQuote, getMultipleQuotes, clearCache } from '../services/MarketDataService';

// Market Status Helper
const getMarketStatus = () => {
  const now = new Date();
  const nyTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hours = nyTime.getHours();
  const minutes = nyTime.getMinutes();
  const day = nyTime.getDay();
  const currentMinutes = hours * 60 + minutes;
  
  if (day === 0 || day === 6) {
    return { status: 'closed', label: 'Closed', sublabel: 'Weekend', color: '#6b7280' };
  }
  
  if (currentMinutes >= 240 && currentMinutes < 570) {
    const opensIn = 570 - currentMinutes;
    const h = Math.floor(opensIn / 60);
    const m = opensIn % 60;
    return { status: 'premarket', label: 'Pre-Market', sublabel: `Opens in ${h}h ${m}m`, color: '#f59e0b' };
  }
  
  if (currentMinutes >= 570 && currentMinutes < 960) {
    const closesIn = 960 - currentMinutes;
    const h = Math.floor(closesIn / 60);
    const m = closesIn % 60;
    return { status: 'open', label: 'Market Open', sublabel: `Closes in ${h}h ${m}m`, color: '#10b981' };
  }
  
  if (currentMinutes >= 960 && currentMinutes < 1200) {
    return { status: 'afterhours', label: 'After-Hours', sublabel: 'Extended trading', color: '#8b5cf6' };
  }
  
  return { status: 'closed', label: 'Closed', sublabel: 'Opens 9:30 AM ET', color: '#6b7280' };
};

const MarketTicker = ({ indices }) => {
  return (
    <div className="market-ticker">
      <div className="ticker-track">
        {[...indices, ...indices].map((item, idx) => (
          <div key={idx} className="ticker-item">
            <span className="ticker-symbol">{item.symbol}</span>
            <span className="ticker-price">{item.price}</span>
            <span className={`ticker-change ${item.changePercent >= 0 ? 'positive' : 'negative'}`}>
              {item.changePercent >= 0 ? '+' : ''}{item.changePercent?.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketIndicesBar = ({ indices, loading }) => {
  return (
    <div className="indices-bar">
      {loading ? (
        <div className="indices-loading">
          <RefreshCw size={14} className="spinning" />
          <span>Loading indices...</span>
        </div>
      ) : (
        indices.map((index) => (
          <div key={index.symbol} className="index-card">
            <div className="index-header">
              <span className="index-symbol">{index.symbol}</span>
              {index.changePercent >= 0 ? (
                <ArrowUpRight size={14} className="positive" />
              ) : (
                <ArrowDownRight size={14} className="negative" />
              )}
            </div>
            <div className="index-price">{index.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className={`index-change ${index.changePercent >= 0 ? 'positive' : 'negative'}`}>
              {index.changePercent >= 0 ? '+' : ''}{index.change?.toFixed(2)} ({index.changePercent >= 0 ? '+' : ''}{index.changePercent?.toFixed(2)}%)
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const SectorHeatmap = ({ sectors, onSectorClick }) => {
  const getHeatColor = (change) => {
    if (change >= 3) return '#059669';
    if (change >= 2) return '#10b981';
    if (change >= 1) return '#34d399';
    if (change >= 0.5) return '#6ee7b7';
    if (change > 0) return '#a7f3d0';
    if (change > -0.5) return '#fecaca';
    if (change > -1) return '#f87171';
    if (change > -2) return '#ef4444';
    if (change > -3) return '#dc2626';
    return '#b91c1c';
  };

  const getTextColor = (change) => {
    return Math.abs(change) > 1.5 ? '#ffffff' : 'rgba(255,255,255,0.9)';
  };

  return (
    <div className="sector-heatmap">
      {sectors.map((sector) => (
        <div 
          key={sector.symbol} 
          className="heatmap-cell"
          style={{ 
            backgroundColor: getHeatColor(sector.changePercent),
            color: getTextColor(sector.changePercent)
          }}
          onClick={() => onSectorClick(sector.symbol)}
        >
          <span className="heatmap-name">{sector.name}</span>
          <span className="heatmap-change">
            {sector.changePercent >= 0 ? '+' : ''}{sector.changePercent?.toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  );
};

function MarketsPage({ onOpenModal }) {
  const { t, formatPrice } = useUserPreferences();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trending');
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [marketCapFilter, setMarketCapFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showCapDropdown, setShowCapDropdown] = useState(false);
  const filterRef = useRef(null);
  const capRef = useRef(null);

  const [marketStatus, setMarketStatus] = useState(getMarketStatus());
  const [loading, setLoading] = useState(true);
  const [indicesLoading, setIndicesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [discoverLoaded, setDiscoverLoaded] = useState(false);
  const [marketIndices, setMarketIndices] = useState([]);
  
  const [stockData, setStockData] = useState({
    trending: [],
    gainers: [],
    losers: [],
    sectors: [],
    discover: []
  });

  const [stockNews, setStockNews] = useState([]);
  const [economicNews, setEconomicNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  const indexSymbols = ['SPY', 'QQQ', 'DIA', 'IWM', 'VIX'];
  
  const indexNames = {
    'SPY': 'S&P 500',
    'QQQ': 'NASDAQ',
    'DIA': 'DOW',
    'IWM': 'Russell 2K',
    'VIX': 'VIX'
  };

  const stockLists = {
    trending: [
      'NVDA', 'TSLA', 'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'AMD', 'PLTR', 'AVGO',
      'NFLX', 'CRM', 'ORCL', 'COIN', 'MSTR', 'SPY', 'QQQ', 'INTC', 'MU', 'QCOM'
    ],
    gainers: [],
    losers: [],
    sectors: [
      'XLK', 'XLV', 'XLF', 'XLY', 'XLP', 'XLE', 'XLI', 'XLB', 'XLU', 'XLRE', 'XLC'
    ],
    discover: [
      'SMCI', 'ARM', 'AI', 'SOUN', 'BBAI', 'IONQ', 'RGTI', 'QUBT', 'PATH', 'UPST',
      'CRWD', 'PANW', 'ZS', 'NET', 'FTNT', 'S', 'OKTA', 'CYBR',
      'SNOW', 'DDOG', 'MDB', 'CFLT', 'GTLB', 'DOCN', 'ESTC', 'NEWR',
      'COIN', 'HOOD', 'AFRM', 'SOFI', 'NU', 'BILL', 'TOST', 'FOUR',
      'RBLX', 'U', 'TTD', 'HUBS', 'SHOP', 'SQ', 'ROKU', 'PINS',
      'RIVN', 'LCID', 'NIO', 'XPEV', 'LI', 'ENPH', 'SEDG', 'FSLR'
    ]
  };

  const sectorNames = {
    'XLK': 'Technology',
    'XLV': 'Healthcare', 
    'XLF': 'Financials',
    'XLY': 'Consumer Disc.',
    'XLP': 'Consumer Staples',
    'XLE': 'Energy',
    'XLI': 'Industrials',
    'XLB': 'Materials',
    'XLU': 'Utilities',
    'XLRE': 'Real Estate',
    'XLC': 'Communication'
  };

  const marketCapCategories = {
    'NVDA': 'large', 'TSLA': 'large', 'AAPL': 'large', 'MSFT': 'large', 'GOOGL': 'large',
    'AMZN': 'large', 'META': 'large', 'AMD': 'large', 'AVGO': 'large', 'NFLX': 'large',
    'CRM': 'large', 'ORCL': 'large', 'INTC': 'large', 'QCOM': 'large', 'MU': 'mid',
    'PLTR': 'mid', 'COIN': 'mid', 'MSTR': 'mid', 'CRWD': 'large', 'PANW': 'large',
    'SNOW': 'mid', 'DDOG': 'mid', 'NET': 'mid', 'SHOP': 'large', 'SQ': 'mid',
    'SMCI': 'mid', 'ARM': 'large', 'HOOD': 'small', 'AFRM': 'small', 'SOFI': 'small',
    'IONQ': 'small', 'RGTI': 'small', 'QUBT': 'small', 'SOUN': 'small', 'BBAI': 'small',
    'RIVN': 'mid', 'LCID': 'small', 'NIO': 'mid', 'RBLX': 'mid', 'U': 'mid'
  };

  const fetchIndices = useCallback(async () => {
    setIndicesLoading(true);
    try {
      const quotes = await getMultipleQuotes(indexSymbols);
      const indicesData = quotes.map(q => ({
        symbol: indexNames[q.symbol] || q.symbol,
        rawSymbol: q.symbol,
        price: q.price,
        change: q.change,
        changePercent: q.changePercent
      }));
      setMarketIndices(indicesData);
    } catch (error) {
      console.error('Failed to fetch indices:', error);
    } finally {
      setIndicesLoading(false);
    }
  }, []);

  const fetchMarketData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
      clearCache();
      setDiscoverLoaded(false);
    } else {
      setLoading(true);
    }

    try {
      const trendingQuotes = await getMultipleQuotes(stockLists.trending);
      const trendingData = trendingQuotes.map(q => ({
        symbol: q.symbol,
        name: q.name,
        type: 'stocks',
        marketCap: marketCapCategories[q.symbol] || 'mid',
        price: q.price,
        change: q.change,
        changePercent: q.changePercent,
        volume: formatVolume(q.volume),
        sparklineData: generateSparklineData(q.changePercent)
      }));

      const gainersData = [...trendingData]
        .filter(s => s.changePercent > 0)
        .sort((a, b) => b.changePercent - a.changePercent);

      const losersData = [...trendingData]
        .filter(s => s.changePercent < 0)
        .sort((a, b) => a.changePercent - b.changePercent);

      const sectorQuotes = await getMultipleQuotes(stockLists.sectors);
      const sectorsData = sectorQuotes.map(q => ({
        symbol: q.symbol,
        name: sectorNames[q.symbol] || q.name,
        type: 'etfs',
        marketCap: 'large',
        price: q.price,
        change: q.change,
        changePercent: q.changePercent,
        volume: formatVolume(q.volume),
        sparklineData: generateSparklineData(q.changePercent)
      }));

      setStockData(prev => ({
        ...prev,
        trending: trendingData,
        gainers: gainersData,
        losers: losersData,
        sectors: sectorsData.sort((a, b) => b.changePercent - a.changePercent),
      }));

    } catch (error) {
      console.error('Failed to fetch market data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const fetchDiscoverData = useCallback(async () => {
    if (discoverLoaded) return;
    
    try {
      const discoverQuotes = await getMultipleQuotes(stockLists.discover);
      const discoverData = discoverQuotes.map(q => ({
        symbol: q.symbol,
        name: q.name,
        type: 'stocks',
        marketCap: marketCapCategories[q.symbol] || 'small',
        price: q.price,
        change: q.change,
        changePercent: q.changePercent,
        volume: formatVolume(q.volume),
        sparklineData: generateSparklineData(q.changePercent)
      }));

      setStockData(prev => ({
        ...prev,
        discover: discoverData
      }));
      setDiscoverLoaded(true);
    } catch (error) {
      console.error('Failed to fetch discover data:', error);
    }
  }, [discoverLoaded]);

  const fetchNews = useCallback(async () => {
    setNewsLoading(true);
    try {
      const mockStockNews = [
        { id: 1, title: 'NVIDIA Announces Next-Gen AI Chips, Stock Surges 8%', source: 'Reuters', time: '1h ago', ticker: 'NVDA', breaking: true, sentiment: 'bullish' },
        { id: 2, title: 'Tesla Deliveries Beat Q4 Estimates by 15%', source: 'Bloomberg', time: '2h ago', ticker: 'TSLA', breaking: true, sentiment: 'bullish' },
        { id: 3, title: 'Apple Vision Pro Sales Below Expectations', source: 'CNBC', time: '3h ago', ticker: 'AAPL', breaking: false, sentiment: 'bearish' },
        { id: 4, title: 'Microsoft Azure Revenue Grows 29% YoY', source: 'WSJ', time: '4h ago', ticker: 'MSFT', breaking: false, sentiment: 'bullish' },
        { id: 5, title: 'Amazon Expands Same-Day Delivery Network', source: 'TechCrunch', time: '5h ago', ticker: 'AMZN', breaking: false, sentiment: 'neutral' },
        { id: 6, title: 'Meta AI Assistant Reaches 500M Users', source: 'Verge', time: '6h ago', ticker: 'META', breaking: false, sentiment: 'bullish' },
      ];

      const mockEconomicNews = [
        { id: 1, title: 'Fed Signals Rate Cut in Early 2025 Meeting', source: 'Reuters', time: '1h ago', breaking: true, sentiment: 'bullish' },
        { id: 2, title: 'US Inflation Falls to 2.4%, Below Forecast', source: 'Bloomberg', time: '2h ago', breaking: true, sentiment: 'bullish' },
        { id: 3, title: 'Treasury Yields Drop on Weak Economic Data', source: 'CNBC', time: '3h ago', breaking: false, sentiment: 'neutral' },
        { id: 4, title: 'China Manufacturing PMI Contracts Again', source: 'FT', time: '4h ago', breaking: false, sentiment: 'bearish' },
        { id: 5, title: 'ECB Holds Rates, Signals Data Dependency', source: 'Reuters', time: '5h ago', breaking: false, sentiment: 'neutral' },
        { id: 6, title: 'US Jobs Report Shows Cooling Labor Market', source: 'WSJ', time: '6h ago', breaking: false, sentiment: 'neutral' },
      ];

      setStockNews(mockStockNews);
      setEconomicNews(mockEconomicNews);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setNewsLoading(false);
    }
  }, []);

  const formatVolume = (vol) => {
    if (!vol) return '--';
    if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B`;
    if (vol >= 1e6) return `${(vol / 1e6).toFixed(1)}M`;
    if (vol >= 1e3) return `${(vol / 1e3).toFixed(1)}K`;
    return vol.toString();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setMarketStatus(getMarketStatus());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchIndices();
    fetchMarketData();
    fetchNews();
  }, [fetchIndices, fetchMarketData, fetchNews]);

  useEffect(() => {
    if (activeTab === 'discover' && !discoverLoaded) {
      fetchDiscoverData();
    }
  }, [activeTab, discoverLoaded, fetchDiscoverData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
      if (capRef.current && !capRef.current.contains(e.target)) {
        setShowCapDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterOptions = [
    { id: 'all', label: 'All Assets' },
    { id: 'stocks', label: 'Stocks' },
    { id: 'etfs', label: 'ETFs' },
  ];

  const capOptions = [
    { id: 'all', label: 'All Caps', icon: <Layers size={12} /> },
    { id: 'large', label: 'Large Cap', icon: <DollarSign size={12} /> },
    { id: 'mid', label: 'Mid Cap', icon: <DollarSign size={12} /> },
    { id: 'small', label: 'Small Cap', icon: <DollarSign size={12} /> },
  ];

  const tabs = [
    { id: 'trending', label: 'Trending', icon: <Flame size={14} /> },
    { id: 'gainers', label: 'Top Gainers', icon: <TrendingUp size={14} /> },
    { id: 'losers', label: 'Top Losers', icon: <TrendingDown size={14} /> },
    { id: 'sectors', label: 'Sectors', icon: <Building2 size={14} /> },
    { id: 'discover', label: 'Discover', icon: <Compass size={14} /> },
  ];

  // Navigate to stock research page (row click)
  const handleStockClick = (symbol) => {
    navigate(`/stock/${symbol}`);
  };

  // Navigate to fullscreen chart (chart button)
  const handleViewChart = (symbol, e) => {
    e.stopPropagation();
    navigate(`/chart/${symbol}`);
  };

  const handleFilterSelect = (filterId) => {
    setSelectedFilter(filterId);
    setShowFilterDropdown(false);
  };
  
  const handleCapSelect = (capId) => {
    setMarketCapFilter(capId);
    setShowCapDropdown(false);
  };
  
  const handleRefresh = () => {
    fetchIndices();
    fetchMarketData(true);
  };

  const filteredStocks = stockData[activeTab]?.filter(stock => {
    const matchesSearch = stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      stock.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedFilter === 'all' || stock.type === selectedFilter;
    const matchesCap = marketCapFilter === 'all' || stock.marketCap === marketCapFilter;
    return matchesSearch && matchesType && matchesCap;
  }) || [];

  const currentFilterLabel = filterOptions.find(f => f.id === selectedFilter)?.label || 'All Assets';
  const currentCapLabel = capOptions.find(c => c.id === marketCapFilter)?.label || 'All Caps';

  const getSentimentBadge = (sentiment) => {
    switch (sentiment) {
      case 'bullish':
        return <span className="sentiment-badge bullish"><TrendingUp size={10} /> {t('bullish')}</span>;
      case 'bearish':
        return <span className="sentiment-badge bearish"><TrendingDown size={10} /> {t('bearish')}</span>;
      default:
        return <span className="sentiment-badge neutral"><Activity size={10} /> {t('neutral')}</span>;
    }
  };

  return (
    <div className="markets-page">
      <MarketTicker indices={marketIndices} />

      <div className="page-header">
        <div className="page-title-section">
          <div className="title-row">
            <h1>{t('markets')}</h1>
            <div className="market-status" style={{ '--status-color': marketStatus.color }}>
              <span className="status-dot"></span>
              <span className="status-label">{marketStatus.label}</span>
              <span className="status-sublabel">{marketStatus.sublabel}</span>
            </div>
          </div>
          <p>Real-time market data and analysis</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <List size={16} />
            </button>
            <button 
              className={`view-btn ${viewMode === 'heatmap' ? 'active' : ''}`}
              onClick={() => setViewMode('heatmap')}
              title="Heatmap View"
              disabled={activeTab !== 'sectors'}
            >
              <Grid3X3 size={16} />
            </button>
          </div>
          <button 
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <MarketIndicesBar indices={marketIndices} loading={indicesLoading} />

      <div className="controls-row">
        <div className="tab-buttons">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="search-filter-group">
          <div className="search-box">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search stocks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-dropdown" ref={capRef}>
            <button className="filter-btn cap-btn" onClick={() => setShowCapDropdown(!showCapDropdown)}>
              <Layers size={14} />
              {currentCapLabel}
              <ChevronDown size={14} />
            </button>
            
            {showCapDropdown && (
              <div className="filter-menu">
                {capOptions.map(option => (
                  <button
                    key={option.id}
                    className={`filter-option ${marketCapFilter === option.id ? 'selected' : ''}`}
                    onClick={() => handleCapSelect(option.id)}
                  >
                    {option.icon}
                    {option.label}
                    {marketCapFilter === option.id && <Check size={12} />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="filter-dropdown" ref={filterRef}>
            <button className="filter-btn" onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
              <Filter size={14} />
              {currentFilterLabel}
              <ChevronDown size={14} />
            </button>
            
            {showFilterDropdown && (
              <div className="filter-menu">
                {filterOptions.map(option => (
                  <button
                    key={option.id}
                    className={`filter-option ${selectedFilter === option.id ? 'selected' : ''}`}
                    onClick={() => handleFilterSelect(option.id)}
                  >
                    {option.label}
                    {selectedFilter === option.id && <Check size={12} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="content-grid">
        <div className="table-container">
          {loading || (activeTab === 'discover' && !discoverLoaded) ? (
            <div className="loading-state">
              <RefreshCw size={20} className="spinning" />
              <p>{activeTab === 'discover' ? 'Loading discover stocks...' : 'Loading market data...'}</p>
            </div>
          ) : viewMode === 'heatmap' && activeTab === 'sectors' ? (
            <SectorHeatmap 
              sectors={stockData.sectors} 
              onSectorClick={(symbol) => handleStockClick(symbol)}
            />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>SYMBOL</th>
                  <th>NAME</th>
                  <th>CHART</th>
                  <th>{t('price').toUpperCase()}</th>
                  <th>{t('change').toUpperCase()}</th>
                  <th>{t('volume').toUpperCase()}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredStocks.map((stock) => (
                  <tr key={stock.symbol} className="table-row" onClick={() => handleStockClick(stock.symbol)}>
                    <td>
                      <span className="symbol">{stock.symbol}</span>
                      {stock.marketCap === 'small' && <span className="cap-badge small">S</span>}
                      {stock.marketCap === 'mid' && <span className="cap-badge mid">M</span>}
                    </td>
                    <td><span className="name">{stock.name}</span></td>
                    <td>
                      <Sparkline 
                        data={stock.sparklineData} 
                        color={stock.changePercent >= 0 ? '#10b981' : '#ef4444'}
                        width={60}
                        height={24}
                      />
                    </td>
                    <td>{formatPrice(stock.price)}</td>
                    <td className={stock.changePercent >= 0 ? 'positive' : 'negative'}>
                      <span className="change-value">{stock.changePercent >= 0 ? '+' : ''}{stock.change?.toFixed(2)}</span>
                      <span className="change-percent">({stock.changePercent >= 0 ? '+' : ''}{stock.changePercent?.toFixed(2)}%)</span>
                    </td>
                    <td className="volume">{stock.volume}</td>
                    <td>
                      <button className="chart-btn" onClick={(e) => handleViewChart(stock.symbol, e)} title="Open Full Chart">
                        <ExternalLink size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="news-sidebar">
          <div className="news-panel">
            <div className="news-header">
              <Newspaper size={14} />
              <span>Stock News</span>
              <span className="live-badge">LIVE</span>
            </div>
            <div className="news-list">
              {stockNews.map(item => (
                <div key={item.id} className={`news-item ${item.breaking ? 'breaking' : ''}`} onClick={() => handleStockClick(item.ticker)}>
                  {item.breaking && <span className="breaking-dot" />}
                  <div className="news-content">
                    <p className="news-title">{item.title}</p>
                    <div className="news-meta">
                      <span className="ticker">${item.ticker}</span>
                      {getSentimentBadge(item.sentiment)}
                      <span className="news-time">{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="news-panel">
            <div className="news-header">
              <Globe size={14} />
              <span>Economic News</span>
              <span className="live-badge">LIVE</span>
            </div>
            <div className="news-list">
              {economicNews.map(item => (
                <div key={item.id} className={`news-item ${item.breaking ? 'breaking' : ''}`}>
                  {item.breaking && <span className="breaking-dot" />}
                  <div className="news-content">
                    <p className="news-title">{item.title}</p>
                    <div className="news-meta">
                      {getSentimentBadge(item.sentiment)}
                      <span className="news-source">{item.source}</span>
                      <span className="news-time">{item.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketsPage;